import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../../hooks/useLocale'
import { useAuth } from '../../hooks/useAuth'
import { FormField, LanguageSwitcher } from '../shared'
import api from '../../services/api'

/**
 * PreferencesTab Component
 *
 * User preferences management section.
 *
 * Features:
 * - Language selection (fully functional)
 * - Email notification preferences (Brevo list management)
 *   - Product updates list
 *   - Marketing emails list
 *
 * Planned features:
 * - Theme selection (light/dark)
 * - Timezone settings
 * - Data export options
 */
function PreferencesTab() {
  const { t, i18n } = useTranslation(['profile', 'common'])
  const { currentLanguage } = useLocale()
  const { user } = useAuth()

  // Local state for preferences
  const [preferences, setPreferences] = useState({
    language: currentLanguage,
    emailNotifications: {
      productUpdates: false,
      marketingEmails: false
    }
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true)

  // Load email subscriptions on mount
  useEffect(() => {
    const loadSubscriptions = async () => {
      if (!user) return

      try {
        const response = await api.get('/preferences/email-subscriptions')
        if (response.data.success) {
          setPreferences(prev => ({
            ...prev,
            emailNotifications: {
              productUpdates: response.data.productUpdates,
              marketingEmails: response.data.marketingEmails
            }
          }))
        }
      } catch (error) {
        console.error('Failed to load email subscriptions:', error)
        // Keep default values
      } finally {
        setIsLoadingSubscriptions(false)
      }
    }

    loadSubscriptions()
  }, [user])

  // Handle language change (called by LanguageSwitcher onChange)
  const handleLanguageChange = useCallback((newLang) => {
    setPreferences(prev => ({ ...prev, language: newLang }))

    // Show success message
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 3000)
  }, [])

  // Handle checkbox changes
  const handleCheckboxChange = useCallback(async (category, field) => {
    if (category !== 'emailNotifications') return

    // Clear previous errors
    setSubmitError(null)

    // Optimistic update
    const newValue = !preferences[category][field]
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: newValue
      }
    }))

    // Save to backend
    setIsSubmitting(true)
    try {
      await api.post('/preferences/email-subscriptions', {
        productUpdates: field === 'productUpdates' ? newValue : preferences.emailNotifications.productUpdates,
        marketingEmails: field === 'marketingEmails' ? newValue : preferences.emailNotifications.marketingEmails
      })

      // Show success message
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to update email subscriptions:', error)

      // Show error to user
      const errorMsg = error?.response?.data?.error || error?.message || t('profile:preferences.error.update')
      setSubmitError(errorMsg)

      // Revert optimistic update
      setPreferences(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [field]: !newValue
        }
      }))
    } finally {
      setIsSubmitting(false)
    }
  }, [preferences, t])

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium">{t('profile:preferences.success')}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-sm">{t('profile:preferences.error.title')}</p>
              <p className="text-sm mt-1">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Language Selection */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('profile:preferences.language.title')}
        </h3>
        <FormField
          label={t('profile:preferences.language.label')}
          helperText={t('profile:preferences.language.helper')}
        >
          <LanguageSwitcher
            variant="full"
            dropdownAlign="left"
            onChange={handleLanguageChange}
          />
        </FormField>
      </div>

      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('profile:preferences.notifications.title')}
        </h3>
        <div className="space-y-4">
          <label className="flex items-start cursor-pointer group">
            <input
              type="checkbox"
              checked={preferences.emailNotifications.productUpdates}
              onChange={() => handleCheckboxChange('emailNotifications', 'productUpdates')}
              disabled={isLoadingSubscriptions || isSubmitting}
              className="mt-1 w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              aria-describedby="product-updates-desc"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                {t('profile:preferences.notifications.productUpdates.label')}
              </span>
              <p id="product-updates-desc" className="text-xs text-gray-500 mt-0.5">
                {t('profile:preferences.notifications.productUpdates.description')}
              </p>
            </div>
          </label>

          <label className="flex items-start cursor-pointer group">
            <input
              type="checkbox"
              checked={preferences.emailNotifications.marketingEmails}
              onChange={() => handleCheckboxChange('emailNotifications', 'marketingEmails')}
              disabled={isLoadingSubscriptions || isSubmitting}
              className="mt-1 w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              aria-describedby="marketing-emails-desc"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                {t('profile:preferences.notifications.marketingEmails.label')}
              </span>
              <p id="marketing-emails-desc" className="text-xs text-gray-500 mt-0.5">
                {t('profile:preferences.notifications.marketingEmails.description')}
              </p>
            </div>
          </label>
        </div>
      </div>

    </div>
  )
}

export default PreferencesTab